import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { getAttribution, clearAttribution } from '../utils/attribution';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Global axios interceptor: auto-logout on 401 "User not found"
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && error.response?.data?.detail === 'User not found') {
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
          window.location.href = '/auth';
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          // Only clear token on explicit auth failures (401), not network errors
          if (error.response?.status === 401) {
            localStorage.removeItem('token');
            setToken(null);
          }
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password, invite_code = null, funnel = null) => {
    // Anti-bot: fetch PoW challenge and solve it
    let pow_challenge = null, pow_ts = null, pow_solution = null;
    try {
      const challengeRes = await axios.get(`${API}/auth/challenge`);
      const { challenge, ts } = challengeRes.data;
      pow_challenge = challenge;
      pow_ts = ts;
      // Solve: find a nonce where SHA256(challenge + nonce) starts with "00"
      for (let i = 0; i < 1000000; i++) {
        const attempt = String(i);
        const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(challenge + attempt));
        const hex = Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
        if (hex.startsWith('00')) { pow_solution = attempt; break; }
      }
    } catch {}

    const payload = { name, email, password, website: '', pow_challenge, pow_ts, pow_solution };
    if (invite_code) payload.invite_code = invite_code;
    if (funnel) payload.funnel = funnel;
    // Forward first-touch marketing attribution captured by the landing page
    try {
      const attr = getAttribution();
      if (attr && typeof attr === 'object') {
        ['utm_source','utm_medium','utm_campaign','utm_content','utm_term',
         'ttclid','fbclid','gclid','referrer','landing_path'].forEach((k) => {
          if (attr[k]) payload[k] = attr[k];
        });
      }
    } catch (_) {}
    const response = await axios.post(`${API}/auth/register`, payload);
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    // Belt-and-suspenders: persist the funnel so the onboarding page can detect
    // it even if a re-render races the user-state hydration.
    try {
      if (userData?.signup_funnel) {
        localStorage.setItem('signup_funnel', userData.signup_funnel);
      }
    } catch (_) {}
    // Attribution has been captured server-side; clear so it doesn't leak
    // to a second signup on a shared device.
    clearAttribution();
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('demo_mode');
    localStorage.removeItem('signup_funnel');
    try { sessionStorage.clear(); } catch {}
    setToken(null);
    setUser(null);
  };

  // Used by the Emergent Google OAuth callback flow.
  // The backend returns { token, user } after exchanging the session_id.
  const setAuthFromResponse = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${token}`
  });

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      logout,
      setAuthFromResponse,
      getAuthHeaders,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};
